import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import Icon from './Icon.jsx';

// Per-entity API keys for the public surface (/api/v1 + the MCP server for AI
// agents). Same component on both surfaces via the scope prop (MailTemplateEditor
// pattern): 'admin-client' (Admin → client → Integrations) | 'my' (Settings →
// Integrations). The secret is shown ONCE at creation — after that only a masked
// hint ever comes back from the server.
export default function ApiKeysCard({ entityId, scope = 'my' }) {
  const listFn = scope === 'admin-client' ? () => api.listEntityApiKeys(entityId) : () => api.listMyApiKeys(entityId);
  const createFn = scope === 'admin-client' ? (p) => api.createEntityApiKey(entityId, p) : (p) => api.createMyApiKey(entityId, p);
  const revokeFn = scope === 'admin-client' ? (id) => api.revokeEntityApiKey(entityId, id) : (id) => api.revokeMyApiKey(entityId, id);

  const [keys, setKeys] = useState(null);
  const [personal, setPersonal] = useState([]); // team members' own AI connections (integrations.api_personal)
  const [personalOn, setPersonalOn] = useState(false);
  const [enabled, setEnabled] = useState(false); // per-client master switch (Howler-controlled)
  const [denied, setDenied] = useState(false);
  const [name, setName] = useState('');
  const [withRows, setWithRows] = useState(false);
  const [withDrafts, setWithDrafts] = useState(false);
  const [creating, setCreating] = useState(false);
  const [fresh, setFresh] = useState(null); // { name, secret } — shown once
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setFresh(null);
    listFn().then((r) => { setKeys(r.keys || []); setEnabled(!!r.enabled); setPersonal(r.personal || []); setPersonalOn(!!r.personalEnabled); }).catch(() => { setKeys([]); setDenied(true); });
  }, [entityId, scope]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!keys) return null;
  if (denied) return null; // no integrations.manage on this client — keep the section quiet

  const toggleAccess = async (on) => {
    try {
      const r = await api.setEntityApiAccess(entityId, on);
      setEnabled(!!r.enabled);
    } catch (e) { alert('Couldn’t update API access: ' + e.message); }
  };

  const create = async () => {
    setCreating(true);
    try {
      const scopes = ['read', ...(withRows ? ['read_rows'] : []), ...(withDrafts ? ['write'] : [])];
      const r = await createFn({ name: name.trim() || 'Untitled key', scopes });
      setFresh({ name: r.key.name, secret: r.secret });
      setName('');
      setWithRows(false);
      setWithDrafts(false);
      setKeys((await listFn()).keys || []);
    } catch (e) { alert('Couldn’t create the key: ' + e.message); }
    setCreating(false);
  };
  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"? Anything using it stops working immediately.`)) return;
    try { await revokeFn(k.id); const r = await listFn(); setKeys(r.keys || []); setPersonal(r.personal || []); }
    catch (e) { alert('Couldn’t revoke the key: ' + e.message); }
  };
  const copy = () => {
    (navigator.clipboard?.writeText(fresh.secret) || Promise.reject()).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1600); })
      .catch(() => window.prompt('Copy this key:', fresh.secret));
  };

  const active = keys.filter((k) => !k.revokedAt);
  const base = window.location.origin;
  return (
    <div style={card}>
      <div style={title}>🔌 API access &amp; AI agents</div>
      <p style={sub}>
        Read this client’s Pulse data from other tools: a REST API (<code style={inline}>{base}/api/v1</code>) and an
        MCP server for AI agents like Claude (<code style={inline}>{base}/mcp</code>). Keys are read-only, scoped to
        this client only, and sent as <code style={inline}>Authorization: Bearer …</code>.
        {' '}Full guide: <a href="/api-guide" target="_blank" rel="noreferrer" style={{ color: 'var(--brand)' }}>{base}/api-guide</a>
      </p>

      {scope === 'admin-client' && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={enabled} onChange={(e) => toggleAccess(e.target.checked)} style={{ width: 16, height: 16 }} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            API access enabled for this client
            {!enabled && <span style={{ color: 'var(--muted)', fontWeight: 400 }}> — keys can be prepared but won’t work until this is on</span>}
          </span>
        </label>
      )}
      {scope === 'my' && !enabled && (
        <p style={{ ...sub, fontWeight: 600 }}>
          API access isn’t switched on for your account yet — ask your Howler contact to enable it.
        </p>
      )}

      {fresh && (
        <div style={freshBox}>
          <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 6 }}>“{fresh.name}” — copy this key now. It won’t be shown again.</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <code style={{ ...pill, wordBreak: 'break-all' }}>{fresh.secret}</code>
            <button style={btn} onClick={copy}>{copied ? '✓ Copied' : 'Copy'}</button>
            <button style={ghostBtn} onClick={() => setFresh(null)}>Done</button>
          </div>
        </div>
      )}

      {active.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
          {active.map((k) => (
            <div key={k.id} style={row}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{k.name}</div>
                <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                  <code>{k.hint}</code> · {k.scopes.join(' + ')} · {k.lastUsedAt ? `last used ${new Date(k.lastUsedAt).toLocaleDateString()}` : 'never used'}
                </div>
              </div>
              <button style={dangerBtn} onClick={() => revoke(k)}>Revoke</button>
            </div>
          ))}
        </div>
      )}
      {active.length === 0 && !fresh && <p style={{ ...sub, marginBottom: 12 }}>No active keys yet.</p>}

      {(personalOn || personal.some((k) => !k.revokedAt)) && (
        <div style={{ margin: '4px 0 14px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}><Icon name="user" size={14} /> Team members’ personal AI connections</div>
          <p style={{ ...sub, marginBottom: 8 }}>
            Anyone on the team can connect Claude or ChatGPT as <b>themselves</b> — those connections only see what that
            person’s role sees in Pulse.{!personalOn && ' Personal connections are currently switched off for this client, so these don’t work right now.'}
          </p>
          {personal.filter((k) => !k.revokedAt).length === 0 && <p style={{ ...sub, marginBottom: 0 }}>Nobody has connected one yet.</p>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {personal.filter((k) => !k.revokedAt).map((k) => (
              <div key={k.id} style={row}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{k.ownerEmail || 'Unknown person'}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                    {k.name} · {k.scopes.join(' + ')} · {k.lastUsedAt ? `last used ${new Date(k.lastUsedAt).toLocaleDateString()}` : 'never used'}
                  </div>
                </div>
                <button style={dangerBtn} onClick={() => revoke(k)}>Disconnect</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {(scope === 'admin-client' || enabled) && (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              style={input}
              placeholder="Key name (e.g. Reporting bot)"
              value={name}
              maxLength={80}
              onChange={(e) => setName(e.target.value)}
            />
            <button style={btn} onClick={create} disabled={creating}>{creating ? 'Creating…' : '+ New read-only key'}</button>
          </div>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 10, cursor: 'pointer' }}>
            <input type="checkbox" checked={withRows} onChange={(e) => setWithRows(e.target.checked)} style={{ marginTop: 2, width: 16, height: 16 }} />
            <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>
              Also allow <b>row-level data</b> — the table behind a tile (e.g. customer &amp; ticketing records).
              May include personal data; only grant this to tools that genuinely need it.
            </span>
          </label>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={withDrafts} onChange={(e) => setWithDrafts(e.target.checked)} style={{ marginTop: 2, width: 16, height: 16 }} />
            <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>
              Also allow <b>creating drafts</b> — build audience segments, draft campaigns and build draft dashboards.
              Drafts always await human review &amp; approval in Pulse; connected tools can never send.
            </span>
          </label>
        </>
      )}
    </div>
  );
}

const card = { background: 'var(--card)', border: '1px solid var(--hairline)', borderRadius: 12, padding: 16, marginTop: 16 };
const title = { fontSize: 14, fontWeight: 700, marginBottom: 6 };
const sub = { fontSize: 13, color: 'var(--muted)', margin: '0 0 12px' };
const inline = { fontSize: 12, background: 'var(--elevated, #f3f3f5)', borderRadius: 5, padding: '1px 5px' };
const pill = { fontSize: 13, fontWeight: 600, background: 'var(--elevated, #f3f3f5)', border: '1px solid var(--hairline)', borderRadius: 8, padding: '8px 12px', userSelect: 'all' };
const freshBox = { border: '1px solid var(--brand)', borderRadius: 10, padding: 12, marginBottom: 12 };
const row = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, border: '1px solid var(--hairline)', borderRadius: 10, padding: '10px 12px' };
const input = { flex: '1 1 200px', minWidth: 0, border: '1px solid var(--hairline)', borderRadius: 8, padding: '9px 12px', fontSize: 13.5, background: 'var(--card)', color: 'inherit' };
const btn = { border: '1px solid var(--hairline)', background: 'var(--brand)', color: '#fff', borderRadius: 980, padding: '9px 16px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', minHeight: 40 };
const ghostBtn = { ...btn, background: 'transparent', color: 'var(--muted)' };
const dangerBtn = { ...ghostBtn, color: 'var(--danger, #dc2626)', flexShrink: 0 };
